import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  KeyRound, Save, RefreshCw, ShieldCheck, AlertTriangle,
  Eye, EyeOff, Lock, Clock, History,
} from 'lucide-react';

import { passwordPolicyApi } from '@/api/passwordPolicy.api';
import type { UpdatePasswordPolicyRequest } from '@/api/passwordPolicy.api';
import { usePermission } from '@/hooks/usePermission';
import { Permissions }   from '@/constants/permissions';
import { Spinner }       from '@/components/ui/Spinner';
import { cn }            from '@/utils/cn';

// ── Validation schema ──────────────────────────────────────────────────────────

const schema = z.object({
  minLength:                z.number().int().min(6, 'Min 6').max(128, 'Max 128'),
  requireUppercase:         z.boolean(),
  requireLowercase:         z.boolean(),
  requireNumber:            z.boolean(),
  requireSpecialChar:       z.boolean(),
  passwordExpiryDays:       z.number().int().min(0, 'Min 0').max(365, 'Max 365'),
  passwordHistoryCount:     z.number().int().min(0, 'Min 0').max(24, 'Max 24'),
  maxLoginAttempts:         z.number().int().min(3, 'Min 3').max(20, 'Max 20'),
  lockoutDurationMinutes:   z.number().int().min(1, 'Min 1').max(1440, 'Max 1440'),
  forceChangeOnFirstLogin:  z.boolean(),
  preventUsernameInPassword:z.boolean(),
  sessionTimeoutMinutes:    z.number().int().min(5, 'Min 5').max(1440, 'Max 1440'),
});

type FormValues = z.infer<typeof schema>;

// ── Section card ───────────────────────────────────────────────────────────────

const SectionCard = ({
  icon: Icon,
  title,
  description,
  children,
}: {
  icon: React.ElementType;
  title: string;
  description: string;
  children: React.ReactNode;
}) => (
  <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-slate-800 shadow-sm overflow-hidden">
    <div className="flex items-start gap-3 px-5 py-4 border-b border-gray-100 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
      <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
        <Icon size={16} className="text-indigo-600 dark:text-indigo-400" />
      </div>
      <div>
        <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200">{title}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{description}</p>
      </div>
    </div>
    <div className="px-5 py-4 space-y-4">{children}</div>
  </div>
);

// ── Number input ───────────────────────────────────────────────────────────────

const NumInput = ({
  label,
  name,
  suffix,
  min,
  max,
  disabled,
  register,
  error,
}: {
  label: string;
  name: string;
  suffix?: string;
  min: number;
  max: number;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any;
  error?: string;
}) => (
  <div className="flex items-center justify-between gap-4">
    <label className="text-sm text-gray-700 dark:text-gray-300 flex-1">{label}</label>
    <div className="flex items-center gap-2">
      <input
        type="number"
        min={min}
        max={max}
        disabled={disabled}
        {...register(name, { valueAsNumber: true })}
        className={cn(
          'w-24 rounded-lg border px-3 py-1.5 text-sm text-right',
          'focus:outline-none focus:ring-2 focus:ring-indigo-500',
          disabled
            ? 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed border-gray-200 dark:border-gray-700'
            : 'border-gray-300 dark:border-gray-600',
          error ? 'border-red-400' : '',
        )}
      />
      {suffix && <span className="text-xs text-gray-500 dark:text-gray-400 w-16">{suffix}</span>}
    </div>
    {error && <p className="text-xs text-red-500 col-span-full">{error}</p>}
  </div>
);

// ── Toggle row ────────────────────────────────────────────────────────────────

const ToggleRow = ({
  label,
  name,
  disabled,
  register,
}: {
  label: string;
  name: string;
  disabled?: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  register: any;
}) => (
  <div className="flex items-center justify-between">
    <label htmlFor={name} className="text-sm text-gray-700 dark:text-gray-300 cursor-pointer select-none">
      {label}
    </label>
    <label className="relative inline-flex items-center cursor-pointer">
      <input
        id={name}
        type="checkbox"
        disabled={disabled}
        {...register(name)}
        className="sr-only peer"
      />
      <div className={cn(
        'w-9 h-5 rounded-full peer',
        'bg-gray-200 peer-checked:bg-indigo-600',
        'after:content-[\'\'] after:absolute after:top-0.5 after:left-0.5',
        'after:bg-white after:rounded-full after:h-4 after:w-4',
        'after:transition-all peer-checked:after:translate-x-4',
        disabled ? 'opacity-50 cursor-not-allowed' : '',
      )} />
    </label>
  </div>
);

// ── Page ───────────────────────────────────────────────────────────────────────

export const PasswordPolicyPage = () => {
  const { hasPermission } = usePermission();
  const canView = hasPermission(Permissions.ADMIN_PASSWORD_POLICY_VIEW);
  const canEdit = hasPermission(Permissions.ADMIN_PASSWORD_POLICY_EDIT);

  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [toast,    setToast]    = useState<{ type: 'success' | 'error'; msg: string } | null>(null);

  const {
    register, handleSubmit, reset, formState: { errors, isDirty },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const load = async () => {
    setLoading(true);
    try {
      const res = await passwordPolicyApi.getCurrent();
      const p   = res.data.data;
      if (!p) return;
      reset({
        minLength:                p.minLength,
        requireUppercase:         p.requireUppercase,
        requireLowercase:         p.requireLowercase,
        requireNumber:            p.requireNumber,
        requireSpecialChar:       p.requireSpecialChar,
        passwordExpiryDays:       p.passwordExpiryDays,
        passwordHistoryCount:     p.passwordHistoryCount,
        maxLoginAttempts:         p.maxLoginAttempts,
        lockoutDurationMinutes:   p.lockoutDurationMinutes,
        forceChangeOnFirstLogin:  p.forceChangeOnFirstLogin,
        preventUsernameInPassword:p.preventUsernameInPassword,
        sessionTimeoutMinutes:    p.sessionTimeoutMinutes,
      });
    } catch {
      showToast('error', 'Failed to load password policy.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const showToast = (type: 'success' | 'error', msg: string) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 3500);
  };

  const onSubmit = async (values: FormValues) => {
    setSaving(true);
    try {
      const req: UpdatePasswordPolicyRequest = values;
      await passwordPolicyApi.update(req);
      showToast('success', 'Password policy saved successfully.');
      reset(values); // clear dirty state
    } catch {
      showToast('error', 'Failed to save password policy.');
    } finally {
      setSaving(false);
    }
  };

  if (!canView) {
    return (
      <div className="flex items-center justify-center h-64 text-gray-500 dark:text-gray-400">
        <ShieldCheck size={20} className="mr-2" /> You do not have permission to view password policy.
      </div>
    );
  }

  if (loading) {
    return <div className="flex items-center justify-center h-64"><Spinner /></div>;
  }

  const readOnly = !canEdit;

  return (
    <div className="max-w-3xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-indigo-100 dark:bg-indigo-900/30 flex items-center justify-center">
            <KeyRound size={20} className="text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Password Policy</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Configure organisation-wide password and session security rules.
            </p>
          </div>
        </div>

        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800/50"
          >
            <RefreshCw size={14} className={cn(loading && 'animate-spin')} />
            Refresh
          </button>

          {canEdit && (
            <button
              type="button"
              onClick={handleSubmit(onSubmit)}
              disabled={saving || !isDirty}
              className={cn(
                'flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium transition-colors',
                isDirty && !saving
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-100 dark:bg-gray-700 text-gray-400 dark:text-gray-500 cursor-not-allowed',
              )}
            >
              {saving
                ? <><Spinner size="sm" /> Saving…</>
                : <><Save size={14} /> Save Policy</>
              }
            </button>
          )}
        </div>
      </div>

      {/* Toast */}
      {toast && (
        <div className={cn(
          'flex items-center gap-2 rounded-lg px-4 py-3 text-sm',
          toast.type === 'success'
            ? 'bg-green-50 dark:bg-green-900/20 text-green-700 dark:text-green-400 border border-green-200 dark:border-green-800'
            : 'bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-400 border border-red-200 dark:border-red-800',
        )}>
          {toast.type === 'success'
            ? <ShieldCheck size={16} />
            : <AlertTriangle size={16} />
          }
          {toast.msg}
        </div>
      )}

      {readOnly && (
        <div className="flex items-center gap-2 rounded-lg px-4 py-3 text-sm bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 text-amber-700 dark:text-amber-400">
          <AlertTriangle size={16} />
          You have view-only access. Contact an administrator to make changes.
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-5">
        {/* Password Complexity */}
        <SectionCard
          icon={Eye}
          title="Password Complexity"
          description="Minimum requirements for all user passwords."
        >
          <NumInput
            label="Minimum length"
            name="minLength"
            suffix="characters"
            min={6}
            max={128}
            disabled={readOnly}
            register={register}
            error={errors.minLength?.message}
          />
          <div className="border-t border-gray-100 dark:border-gray-800 pt-3 space-y-3">
            <ToggleRow label="Require uppercase letter (A–Z)" name="requireUppercase" disabled={readOnly} register={register} />
            <ToggleRow label="Require lowercase letter (a–z)" name="requireLowercase" disabled={readOnly} register={register} />
            <ToggleRow label="Require number (0–9)"           name="requireNumber"    disabled={readOnly} register={register} />
            <ToggleRow label="Require special character (!@#…)"name="requireSpecialChar" disabled={readOnly} register={register} />
            <ToggleRow label="Prevent username in password"   name="preventUsernameInPassword" disabled={readOnly} register={register} />
          </div>
        </SectionCard>

        {/* Password Lifecycle */}
        <SectionCard
          icon={History}
          title="Password Lifecycle"
          description="Expiry and history settings to enforce regular rotation."
        >
          <NumInput
            label="Password expiry"
            name="passwordExpiryDays"
            suffix="days (0 = never)"
            min={0}
            max={365}
            disabled={readOnly}
            register={register}
            error={errors.passwordExpiryDays?.message}
          />
          <NumInput
            label="Password history"
            name="passwordHistoryCount"
            suffix="previous (0 = off)"
            min={0}
            max={24}
            disabled={readOnly}
            register={register}
            error={errors.passwordHistoryCount?.message}
          />
          <div className="border-t border-gray-100 dark:border-gray-800 pt-3">
            <ToggleRow label="Force password change on first login" name="forceChangeOnFirstLogin" disabled={readOnly} register={register} />
          </div>
        </SectionCard>

        {/* Account Lockout */}
        <SectionCard
          icon={Lock}
          title="Account Lockout"
          description="Protect against brute-force login attacks."
        >
          <NumInput
            label="Max failed login attempts"
            name="maxLoginAttempts"
            suffix="attempts"
            min={3}
            max={20}
            disabled={readOnly}
            register={register}
            error={errors.maxLoginAttempts?.message}
          />
          <NumInput
            label="Lockout duration"
            name="lockoutDurationMinutes"
            suffix="minutes"
            min={1}
            max={1440}
            disabled={readOnly}
            register={register}
            error={errors.lockoutDurationMinutes?.message}
          />
        </SectionCard>

        {/* Session */}
        <SectionCard
          icon={Clock}
          title="Session Management"
          description="Control idle session timeout for all users."
        >
          <NumInput
            label="Session idle timeout"
            name="sessionTimeoutMinutes"
            suffix="minutes"
            min={5}
            max={1440}
            disabled={readOnly}
            register={register}
            error={errors.sessionTimeoutMinutes?.message}
          />
        </SectionCard>

        {/* Sticky save for large screens */}
        {canEdit && isDirty && (
          <div className="sticky bottom-4 flex justify-end">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-1.5 px-5 py-2.5 rounded-xl bg-indigo-600 text-white text-sm font-semibold shadow-lg hover:bg-indigo-700 disabled:opacity-60"
            >
              {saving ? <><Spinner size="sm" /> Saving…</> : <><Save size={14} /> Save Policy</>}
            </button>
          </div>
        )}
      </form>

      {/* Info note */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 px-5 py-4">
        <div className="flex items-center gap-2 mb-2">
          <EyeOff size={14} className="text-gray-400" />
          <span className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">Note</span>
        </div>
        <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
          Password policy changes take effect immediately for all new login attempts and password resets.
          Existing sessions are not affected until the next session timeout cycle.
          All policy changes are recorded in the security audit log.
        </p>
      </div>
    </div>
  );
};

export default PasswordPolicyPage;
